import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import LoadingIcon from 'components/atoms/LoadingIcon'
import { trim, toLower, isObject, keys, isArray, some, startsWith } from 'lodash'
import AddRentalCar from './rentalCarModal'

const PRICE_OPTIONS = {
  KEEP_SAME: 'keepSame',
  CHANGE: 'change',
}

const DISCOUNT_SOURCE = {
  NONE: 'none',
  AJARLEE: 'ajarlee',
  COMPANY: 'company',
}

const getObjectId = value => {
  if (!value) return ''
  if (isObject(value)) return trim(String(value._id || value.id || ''))
  return trim(String(value))
}

/** Coupon is valid only if target company exists in discount.company list. */
const getBookingDiscountCouponContext = (bookingData, targetCompany = null) => {
  const discount = bookingData?.discount
  if (!discount || !isObject(discount)) {
    return { hasDiscount: false, isValidForCompany: false, couponCode: '' }
  }
  const couponCode = trim(String(discount.cupon ?? ''))
  const bookingCompanyId = getObjectId(targetCompany || bookingData?.rentalCar?.company)
  const discountCompaniesRaw = discount.company
  const discountCompanies = isArray(discountCompaniesRaw) ? discountCompaniesRaw : discountCompaniesRaw ? [discountCompaniesRaw] : []
  const isValidForCompany = bookingCompanyId !== '' && some(discountCompanies, company => getObjectId(company) === bookingCompanyId)
  return { hasDiscount: true, isValidForCompany, couponCode }
}

const buildBookingPromoDiscountLabel = (discount, t) => {
  if (!discount || !isObject(discount)) return t('updateBooking.changeCarWizard.step3.promoDiscount')
  const code = trim(String(discount.cupon ?? ''))
  const title = t('updateBooking.changeCarWizard.step3.promoDiscount')
  return code ? `${title} (${code})` : title
}

/** Right-hand “amount” for promo row: flat → discount.amount (OMR); percentage → discount.percentage (%); else computed OMR (incl. 0). */
const getBookingDiscountAmountDisplay = (discount, formatOmr, t, promoDiscountOmrFallback) => {
  if (!discount || !isObject(discount)) return ''
  const typeLower = toLower(String(discount.type ?? ''))
  if (typeLower === 'flat') {
    const a = Number(discount.amount)
    const v = Number.isFinite(a) ? a : 0
    return `-${formatOmr(v)} ${t('common.omr')}`
  }
  if (typeLower === 'percentage') {
    const p = Number(discount.percentage)
    const v = Number.isFinite(p) ? p : 0
    return `${v}%`
  }
  const f = Number(promoDiscountOmrFallback)
  const omr = Number.isFinite(f) && f > 0 ? f : 0
  return `-${formatOmr(omr)} ${t('common.omr')}`
}

/** OMR discount used for “base after discount”: flat uses `amount`, percentage uses current rental total. */
const getBookingDiscountPriceOmr = (discount, rentalTotalOmr, promoDiscountOmr) => {
  if (!discount || !isObject(discount)) return promoDiscountOmr
  const typeLower = toLower(String(discount.type ?? ''))
  if (typeLower === 'flat') {
    const a = Number(discount.amount)
    return Number.isFinite(a) ? a : promoDiscountOmr
  }
  if (typeLower === 'percentage') {
    const p = Number(discount.percentage)
    if (Number.isFinite(p)) return Math.max(0, (Number(rentalTotalOmr) || 0) * (p / 100))
  }
  return promoDiscountOmr
}

/** Old booking list / sticker daily rate (before customer discount), for comparison with new car. */
const getOldBookingListDailyRate = b => {
  if (!b) return 0
  if (b.isDiscountEdit === true) return Number(b.dailyRate) || 0
  const dailyRate = Number(b.dailyRate) || 0
  const dailyDiscountRate = Number(b.dailyDiscountRate) || 0
  const actualDailyRate = Number(b.rentalCarDailyRate) || dailyRate
  if (dailyDiscountRate > 0) return dailyRate
  if (actualDailyRate > dailyRate) return actualDailyRate
  return dailyRate
}

/** Rate actually used for old booking pricing (discounted daily if set, else list daily). Use for “new car cheaper than old” checks. */
const getOldBookingAppliedDailyRate = b => {
  if (!b) return 0
  const dailyDiscountRate = Number(b.dailyDiscountRate) || 0
  const dailyRate = Number(b.dailyRate) || 0
  if (dailyDiscountRate > 0) return dailyDiscountRate
  return dailyRate
}

/** True when the booking carries a real admin/company daily-rate discount that must survive a car swap. */
const hasRealDailyDiscount = b => Number(b?.dailyDiscountRate) > 0 || (isArray(b?.discountControl) && some(b.discountControl, r => r?.target === 'daily' && Number(r?.value) > 0))

/**
 * True when the old booking's low applied per-day rate comes purely from a period tier
 * (weekly/monthly/yearly = weeklyRate/7, monthlyRate/30, yearlyRate/365), NOT from a real admin/company
 * discount. In that case the gap between the car's sticker daily rate and the applied period rate must
 * NOT be treated as a discount when swapping cars — the new car's own period rate should apply instead.
 */
const isOldBookingPeriodRateOnly = b => {
  const dt = toLower(String(b?.bookingDurationType || ''))
  const isPeriod = startsWith(dt, 'weekly') || startsWith(dt, 'monthly') || startsWith(dt, 'yearly')
  return isPeriod && !hasRealDailyDiscount(b)
}

/**
 * True when the NEW car's effective daily rate is below its sticker because the booking duration lands
 * in a period tier (weekly/monthly/yearly): `car.dailyRate` is the base period per-day and
 * `car.actualDailyRate` the sticker, with no in-window daily discount. This sticker-vs-period gap must NOT
 * be turned into a discount on a car swap — the new car's own period rate should apply directly. (An
 * in-window *DiscountRate sets car.dailyDiscountRate, so it is handled as a normal discount, not here.)
 */
const isNewCarPeriodPriced = car => {
  const dailyRate = Number(car?.dailyRate) || 0
  const actualDailyRate = Number(car?.actualDailyRate) || 0
  const dailyDiscountRate = Number(car?.dailyDiscountRate) || 0
  return dailyRate > 0 && actualDailyRate > dailyRate && !(dailyDiscountRate > 0)
}

// vehicles/prices can return HTTP 200 + success:true but with `data.error` (e.g. "Car is not available
// for this rental duration") instead of a real price snapshot. Treat that as an error, not a snapshot.
const readPriceError = resp => (resp?.success && resp?.data && resp.data.error ? String(resp.data.error) : null)

/** Native new-car daily discount rate for API (no old-booking override). */
const getNewCarNativeDiscountRate = car => {
  if (!car) return 0
  const newCarDailyRate = Number(car.dailyRate) || 0
  const newCarDailyDiscountRate = Number(car.dailyDiscountRate) || 0
  const actualDailyRate = Number(car.actualDailyRate) || newCarDailyRate
  if (newCarDailyDiscountRate > 0) return newCarDailyDiscountRate
  if (actualDailyRate > newCarDailyRate) return newCarDailyRate
  return 0
}
const getChangePriceCarAppliedRates = car => {
  if (!car) return { listDaily: 0, discountDaily: 0 }
  const invList = Number(car.invoiceDailyRate)
  const invDisc = Number(car.invoiceDailyDiscountRate)
  if (Number.isFinite(invDisc) && invDisc > 0) {
    if (Number.isFinite(invList) && invList > invDisc) {
      return { listDaily: invList, discountDaily: invDisc }
    }
    const dr = Number(car.dailyRate) || 0
    const actual = Number(car.actualDailyRate) || dr
    const listDaily = actual > dr ? actual : dr
    if (listDaily > 0 && invDisc < listDaily) {
      return { listDaily, discountDaily: invDisc }
    }
  }
  const newCarDailyRate = Number(car.dailyRate) || 0
  const newCarDailyDiscountRate = Number(car.dailyDiscountRate) || 0
  const actualDailyRate = Number(car.actualDailyRate) || newCarDailyRate
  if (newCarDailyDiscountRate > 0) {
    return { listDaily: newCarDailyRate, discountDaily: newCarDailyDiscountRate }
  }
  if (actualDailyRate > newCarDailyRate) {
    return { listDaily: actualDailyRate, discountDaily: newCarDailyRate }
  }
  return { listDaily: newCarDailyRate, discountDaily: newCarDailyDiscountRate || 0 }
}

const applyCarRatesToAjarleeCompanyFields = (car, setDiscountListDaily, setDiscountDiscountDaily) => {
  const { listDaily, discountDaily } = getChangePriceCarAppliedRates(car)
  if (listDaily > 0) {
    setDiscountListDaily(String(listDaily))
  }
  const l = Number(listDaily)
  const d = Number(discountDaily)
  setDiscountDiscountDaily(l > 0 && d > 0 && d < l ? String(discountDaily) : '')
}

const resolveWizardDeliveryType = bookingData => {
  const raw = bookingData?.deliveryType ?? bookingData?.delivery?.type ?? ''
  const s = trim(String(raw))
  if (s) {
    const compact = toLower(s).replace(/[\s_-]/g, '')
    if (compact === 'pickupmyself') return 'PICK_UP_MYSELF'
    if (compact === 'delivertome') return 'DELIVER_TO_ME'
    if (s === 'PICK_UP_MYSELF' || s === 'DELIVER_TO_ME') return s
  }

  const d = bookingData?.delivery
  if (!d || (isObject(d) && keys(d).length === 0)) {
    return 'PICK_UP_MYSELF'
  }

  if (Number(d.deliveryCost) > 0) return 'DELIVER_TO_ME'

  const hasPickup = isArray(d.pickupLocation?.coordinates) && d.pickupLocation.coordinates.length >= 2
  const hasDropoff = isArray(d.dropOffLocation?.coordinates) && d.dropOffLocation.coordinates.length >= 2
  if (hasPickup && hasDropoff) return 'DELIVER_TO_ME'

  return 'PICK_UP_MYSELF'
}

/** If set: discounted delivery OMR must be &gt; 0 and &lt;= list fee (ajarleeDeliveryCost / officeDeliveryCost from prices API). */
const validateWizardDiscountedDeliveryFee = (listFee, discountStr, t) => {
  const ds = trim(String(discountStr))
  if (ds === '') {
    return { skip: true, ok: true, discountedFee: 0, error: '' }
  }
  const list = Number(listFee)
  const disc = parseFloat(ds)
  if (Number.isNaN(disc)) {
    return { skip: false, ok: false, discountedFee: 0, error: t('updateBooking.messages.pleaseEnterValidNumber') }
  }
  if (disc < 0) {
    return {
      skip: false,
      ok: false,
      discountedFee: 0,
      error: t('updateBooking.changeCarWizard.step3.deliveryDiscountedOutOfRange'),
    }
  }
  if (!Number.isFinite(list) || list < 0) {
    return { skip: false, ok: false, discountedFee: 0, error: t('updateBooking.changeCarWizard.step3.deliveryListFeeUnavailable') }
  }
  if (list === 0) {
    return {
      skip: false,
      ok: false,
      discountedFee: 0,
      error: t('updateBooking.changeCarWizard.step3.deliveryDiscountedListFeeZero'),
    }
  }
  if (disc <= 0) {
    return {
      skip: false,
      ok: false,
      discountedFee: 0,
      error: t('updateBooking.changeCarWizard.step3.deliveryDiscountedMustBePositive'),
    }
  }
  if (disc > list) {
    return {
      skip: false,
      ok: false,
      discountedFee: 0,
      error: t('updateBooking.changeCarWizard.step3.deliveryDiscountedExceedsList'),
    }
  }
  return { skip: false, ok: true, discountedFee: disc, error: '' }
}

/** True when step-3 price call should include delivery (Deliver to me). */
const getWizardShouldIncludeDeliveryInPrice = (bookingData, deliveryData, lastCreatedTypeRef) => {
  const hasNewDelivery = !!(deliveryData?.id || deliveryData?._id)
  const oldDeliveryType = resolveWizardDeliveryType(bookingData)
  const newDeliveryTypeRaw = deliveryData?.type || lastCreatedTypeRef?.current
  const newDeliveryType = newDeliveryTypeRaw ? resolveWizardDeliveryType({ deliveryType: newDeliveryTypeRaw, delivery: deliveryData || {} }) : ''
  return (hasNewDelivery && newDeliveryType === 'DELIVER_TO_ME') || (!hasNewDelivery && oldDeliveryType === 'DELIVER_TO_ME')
}

const ChangeCarWizard = ({ bookingData, onSelectCar, loadingCarId, isAdmin, bookingId, onCancelSuccess, onStageCarSwap }) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deliveryData, setDeliveryData] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [priceDecision, setPriceDecision] = useState(PRICE_OPTIONS.KEEP_SAME)
  const [selectedCar, setSelectedCar] = useState(null)
  const [discountSource, setDiscountSource] = useState(DISCOUNT_SOURCE.NONE)
  /** List / sticker daily (step 3); empty = use selected car `dailyRate`. */
  const [discountListDaily, setDiscountListDaily] = useState('')
  /** Customer daily after discount; if set, sent as `dailyDiscountRate` in prices API (must be &lt; list daily). */
  const [discountDiscountDaily, setDiscountDiscountDaily] = useState('')
  const [discountReason, setDiscountReason] = useState('')
  const [step3Prices, setStep3Prices] = useState(null)
  const [step3PricesLoading, setStep3PricesLoading] = useState(false)
  const [step3DiscountError, setStep3DiscountError] = useState('')
  const [step3PriceError, setStep3PriceError] = useState('')
  /** Discounted delivery fee (OMR), parallel to daily discount rate; list fees come from prices API. */
  const [deliveryDiscountAjarleeOmr, setDeliveryDiscountAjarleeOmr] = useState('')
  const [deliveryDiscountCompanyOmr, setDeliveryDiscountCompanyOmr] = useState('')
  const [step3DeliveryFeeDiscountError, setStep3DeliveryFeeDiscountError] = useState('')
  const [carActionInProgressId, setCarActionInProgressId] = useState(null)

  const discountSourceRef = useRef(discountSource)
  discountSourceRef.current = discountSource
  /** Latest daily discount field; effect below must not list this state in deps or clearing the input re-syncs from API and blocks empty delete. */
  const discountDiscountDailyRef = useRef(discountDiscountDaily)
  discountDiscountDailyRef.current = discountDiscountDaily
  const lastCreatedDeliveryTypeRef = useRef(null)
  const prevBookingIdForDeliveryRef = useRef(bookingId)
  const step3PricesRef = useRef(null)
  useEffect(() => {
    step3PricesRef.current = step3Prices
  }, [step3Prices])

  useEffect(() => {
    lastCreatedDeliveryTypeRef.current = null
  }, [bookingId])

  /** Deliver to me: pre-fill discounted delivery inputs from booking so UI and prices API match old booking. */
  useLayoutEffect(() => {
    const bookingIdChanged = prevBookingIdForDeliveryRef.current !== bookingId
    prevBookingIdForDeliveryRef.current = bookingId

    if (currentStep !== 3) return
    const deliver = getWizardShouldIncludeDeliveryInPrice(bookingData, deliveryData, lastCreatedDeliveryTypeRef)
    if (!deliver) {
      if (bookingIdChanged) {
        setDeliveryDiscountAjarleeOmr('')
        setDeliveryDiscountCompanyOmr('')
      }
      return
    }

    const fmt = raw => {
      if (raw == null || raw === '') return ''
      const n = Number(raw)
      return Number.isFinite(n) ? String(parseFloat(n.toFixed(10))) : ''
    }
    const aj = fmt(bookingData?.ajarleeDiscountedDeliveryCost)
    const off = fmt(bookingData?.officeDiscountedDeliveryCost)

    if (bookingIdChanged) {
      setDeliveryDiscountAjarleeOmr(aj)
      setDeliveryDiscountCompanyOmr(off)
      return
    }

    setDeliveryDiscountAjarleeOmr(prev => (prev !== '' ? prev : aj))
    setDeliveryDiscountCompanyOmr(prev => (prev !== '' ? prev : off))
  }, [
    currentStep,
    bookingId,
    bookingData?.ajarleeDiscountedDeliveryCost,
    bookingData?.officeDiscountedDeliveryCost,
    bookingData?.deliveryId,
    bookingData?.delivery?._id,
    deliveryData,
  ])

  /** Keep same price hides "No discount"; coerce legacy NONE to Ajarlee when reaching step 3. */
  useEffect(() => {
    if (currentStep !== 3) return
    if (priceDecision !== PRICE_OPTIONS.KEEP_SAME) return
    if (discountSource !== DISCOUNT_SOURCE.NONE) return
    setDiscountSource(DISCOUNT_SOURCE.AJARLEE)
  }, [currentStep, priceDecision, discountSource])

  useEffect(() => {
    const dr = selectedCar?.dailyRate
    if (!selectedCar?._id || dr == null || dr === '') return

    const oldAppliedDailyRate = getOldBookingAppliedDailyRate(bookingData)
    const newCarDailyRate = Number(selectedCar.dailyRate) || 0
    const newCarIsCheaperThanOldApplied = newCarDailyRate > 0 && newCarDailyRate < oldAppliedDailyRate

    /** Keep same: show new car list in daily rate and old customer rate in daily discount (e.g. 20 / 10); cheaper-car branch matches prior native logic. */
    if (priceDecision === PRICE_OPTIONS.KEEP_SAME && (discountSource === DISCOUNT_SOURCE.AJARLEE || discountSource === DISCOUNT_SOURCE.COMPANY)) {
      if (newCarIsCheaperThanOldApplied) {
        applyCarRatesToAjarleeCompanyFields(selectedCar, setDiscountListDaily, setDiscountDiscountDaily)
      } else {
        const oldEffectiveRate = bookingData?.dailyDiscountRate > 0 ? bookingData.dailyDiscountRate : bookingData?.dailyRate || 0
        setDiscountListDaily(String(dr))
        setDiscountDiscountDaily(oldEffectiveRate > 0 ? String(oldEffectiveRate) : '')
      }
      return
    }

    if (priceDecision === PRICE_OPTIONS.CHANGE && (discountSource === DISCOUNT_SOURCE.AJARLEE || discountSource === DISCOUNT_SOURCE.COMPANY)) {
      applyCarRatesToAjarleeCompanyFields(selectedCar, setDiscountListDaily, setDiscountDiscountDaily)
    } else {
      setDiscountListDaily(String(dr))
      setDiscountDiscountDaily('')
    }
  }, [
    selectedCar?._id,
    selectedCar?.dailyRate,
    selectedCar?.dailyDiscountRate,
    selectedCar?.actualDailyRate,
    selectedCar?.invoiceDailyRate,
    selectedCar?.invoiceDailyDiscountRate,
    priceDecision,
    discountSource,
    bookingData?.dailyRate,
    bookingData?.dailyDiscountRate,
    bookingData?.rentalCarDailyRate,
    bookingData?.isDiscountEdit,
  ])

  /** After prices load, mirror list + discounted daily into fields when API shows a discount and the user has not typed one. */
  useEffect(() => {
    if (currentStep !== 3) return
    if (priceDecision !== PRICE_OPTIONS.CHANGE) return
    if (discountSource !== DISCOUNT_SOURCE.AJARLEE && discountSource !== DISCOUNT_SOURCE.COMPANY) return
    if (!step3Prices || step3PricesLoading) return
    if (trim(String(discountDiscountDailyRef.current)) !== '') return

    const dr = Number(step3Prices.dailyRate ?? selectedCar?.dailyRate)
    const ddrRaw = step3Prices.dailyDiscountRate
    const ddr = ddrRaw != null && ddrRaw !== '' ? Number(ddrRaw) : NaN
    if (!Number.isFinite(dr) || dr <= 0 || !Number.isFinite(ddr) || ddr <= 0 || ddr >= dr) return

    setDiscountListDaily(String(dr))
    setDiscountDiscountDaily(String(ddr))
  }, [currentStep, priceDecision, discountSource, step3Prices, step3PricesLoading, selectedCar?.dailyRate])

  const parseWizardListDaily = () => {
    const raw = trim(String(discountListDaily))
    if (raw === '') {
      return { listDaily: Number(selectedCar?.dailyRate) || 0 }
    }
    const n = parseFloat(raw)
    return { listDaily: Number.isNaN(n) ? NaN : n }
  }

  /** When `discountStr` non-empty: validate and return rate to send as dailyDiscountRate. */
  const validateWizardDiscountDaily = (listDaily, discountStr, { allowDiscountEqualToList = false } = {}) => {
    const ds = trim(String(discountStr))
    if (ds === '') {
      return { skip: true, ok: true, discountedRate: 0, error: '' }
    }
    if (listDaily == null || Number.isNaN(listDaily) || listDaily <= 0) {
      return { skip: false, ok: false, discountedRate: 0, error: t('updateBooking.changeCarWizard.step3.invalidListDaily') }
    }
    const disc = parseFloat(ds)
    if (Number.isNaN(disc)) {
      return { skip: false, ok: false, discountedRate: 0, error: t('updateBooking.messages.pleaseEnterValidNumber') }
    }
    const overList = allowDiscountEqualToList ? disc > listDaily : disc >= listDaily
    if (disc <= 0 || overList) {
      return {
        skip: false,
        ok: false,
        discountedRate: 0,
        error: t('updateBooking.validation.dailyDiscountRateMustBeLessThanDailyRate'),
      }
    }
    return { skip: false, ok: true, discountedRate: disc, error: '' }
  }

  const fetchStep3Prices = useCallback(async () => {
    setStep3PriceError('')
    if (!selectedCar?._id || !bookingData?.rentalStartDate || !bookingData?.rentalEndDate) {
      setStep3Prices(null)
      return
    }

    const startDate = moment.utc(bookingData.rentalStartDate).toDate()
    const endDate = moment.utc(bookingData.rentalEndDate).toDate()
    if (!moment(startDate).isValid() || !moment(endDate).isValid()) {
      setStep3Prices(null)
      return
    }

    const startMoment = moment.utc(startDate)
    const endMoment = moment.utc(endDate)
    const bookingFromFormatted = startMoment.format('YYYY-MM-DD HH:mm:ss')
    const bookingToFormatted = endMoment.format('YYYY-MM-DD HH:mm:ss')

    const newCarDailyRate = selectedCar.dailyRate || 0
    const newCarDailyDiscountRate = selectedCar.dailyDiscountRate || 0
    const actualDailyRate = selectedCar.actualDailyRate || newCarDailyRate
    const oldEffectiveRate = bookingData?.dailyDiscountRate > 0 ? bookingData.dailyDiscountRate : bookingData?.dailyRate || 0
    const oldAppliedDailyRate = getOldBookingAppliedDailyRate(bookingData)
    const newCarIsCheaperThanOldApplied = newCarDailyRate > 0 && newCarDailyRate < oldAppliedDailyRate

    let dailyDiscountRateForApi = 0

    if (priceDecision === PRICE_OPTIONS.KEEP_SAME) {
      setStep3DiscountError('')
      const srcKeep = discountSourceRef.current
      if (srcKeep === DISCOUNT_SOURCE.AJARLEE || srcKeep === DISCOUNT_SOURCE.COMPANY) {
        let usedStep3CustomDiscount = false
        const { listDaily } = parseWizardListDaily()
        const v = validateWizardDiscountDaily(listDaily, discountDiscountDaily, {
          allowDiscountEqualToList: newCarIsCheaperThanOldApplied,
        })
        if (!v.skip) {
          usedStep3CustomDiscount = true
          if (!v.ok) {
            setStep3DiscountError(v.error)
            dailyDiscountRateForApi = 0
          } else if (v.discountedRate > 0) {
            dailyDiscountRateForApi = v.discountedRate
          }
        }
        if (!usedStep3CustomDiscount) {
          if (newCarIsCheaperThanOldApplied) {
            if (newCarDailyDiscountRate && newCarDailyDiscountRate > 0) {
              dailyDiscountRateForApi = newCarDailyDiscountRate
            } else if (actualDailyRate > newCarDailyRate) {
              dailyDiscountRateForApi = newCarDailyRate
            }
          } else {
            dailyDiscountRateForApi = oldEffectiveRate
          }
        }
      } else if (newCarIsCheaperThanOldApplied) {
        // NONE source, cheaper new car. Only a real in-window weekly discount is sent as the discounted
        // per-day rate; a plain weekly (period) tier sends nothing so the backend applies the period rate
        // (weeklyRate/7) with no phantom discount from the sticker-vs-weekly gap.
        if (newCarDailyDiscountRate > 0 && newCarDailyDiscountRate < newCarDailyRate) {
          dailyDiscountRateForApi = newCarDailyDiscountRate
        } else {
          dailyDiscountRateForApi = 0
        }
      } else {
        dailyDiscountRateForApi = oldEffectiveRate
      }
    } else {
      setStep3DiscountError('')
      let usedStep3CustomDiscount = false
      const src = discountSourceRef.current
      if (src === DISCOUNT_SOURCE.AJARLEE || src === DISCOUNT_SOURCE.COMPANY) {
        const { listDaily } = parseWizardListDaily()
        const v = validateWizardDiscountDaily(listDaily, discountDiscountDaily)
        if (!v.skip) {
          usedStep3CustomDiscount = true
          if (!v.ok) {
            setStep3DiscountError(v.error)
            dailyDiscountRateForApi = 0
          } else if (v.discountedRate > 0) {
            dailyDiscountRateForApi = v.discountedRate
          }
        }
      }
      if (!usedStep3CustomDiscount) {
        if (newCarDailyDiscountRate && newCarDailyDiscountRate > 0) {
          dailyDiscountRateForApi = newCarDailyDiscountRate
        } else if (actualDailyRate > newCarDailyRate) {
          dailyDiscountRateForApi = newCarDailyRate
        }
      }
    }

    const shouldIncludeDeliveryInPrice = getWizardShouldIncludeDeliveryInPrice(bookingData, deliveryData, lastCreatedDeliveryTypeRef)
    const skipDelivery = !shouldIncludeDeliveryInPrice

    /** Only input state (no booking fallback) so clearing the field stops sending discounted delivery. Booking prefill is done in useLayoutEffect. */
    const ajStr = trim(String(deliveryDiscountAjarleeOmr))
    const offStr = trim(String(deliveryDiscountCompanyOmr))

    const listUnavailableDeliveryErr = t('updateBooking.changeCarWizard.step3.deliveryListFeeUnavailable')
    setStep3DeliveryFeeDiscountError('')
    const pricesSnap = step3PricesRef.current
    if (shouldIncludeDeliveryInPrice) {
      if (ajStr !== '') {
        const listAjarlee = Number(pricesSnap?.ajarleeDeliveryCost)
        const v = validateWizardDiscountedDeliveryFee(listAjarlee, ajStr, t)
        if (!v.ok && v.error !== listUnavailableDeliveryErr) setStep3DeliveryFeeDiscountError(v.error)
      }
      if (offStr !== '') {
        const listOffice = Number(pricesSnap?.officeDeliveryCost)
        const v = validateWizardDiscountedDeliveryFee(listOffice, offStr, t)
        if (!v.ok && v.error !== listUnavailableDeliveryErr) setStep3DeliveryFeeDiscountError(v.error)
      }
    }

    const discountCouponCtx = getBookingDiscountCouponContext(bookingData, selectedCar?.company)

    const buildPricesParams = snap => {
      const params = {
        rentalCarId: selectedCar._id,
        bookingFrom: bookingFromFormatted,
        bookingTo: bookingToFormatted,
        skipDelivery,
      }
      if (shouldIncludeDeliveryInPrice) {
        params.isDeliveryDiscount = true
        const listAjarlee = Number(snap?.ajarleeDeliveryCost) || 0
        const vAjarlee = validateWizardDiscountedDeliveryFee(listAjarlee, ajStr, t)
        if (vAjarlee.ok && !vAjarlee.skip) {
          params.ajarleeDiscountedDeliveryCost = vAjarlee.discountedFee
        }
        const listOffice = Number(snap?.officeDeliveryCost) || 0
        const vOffice = validateWizardDiscountedDeliveryFee(listOffice, offStr, t)
        if (vOffice.ok && !vOffice.skip) {
          params.officeDiscountedDeliveryCost = vOffice.discountedFee
        }
      }

      const deliveryIdNew = deliveryData?.id || deliveryData?._id
      const deliveryIdOld = bookingData?.deliveryId || bookingData?.delivery?._id
      const hasNewDeliveryForId = !!(deliveryData?.id || deliveryData?._id)
      if (deliveryIdNew) {
        params.deliveryId = deliveryIdNew
      } else if (deliveryIdOld && !hasNewDeliveryForId) {
        params.deliveryId = deliveryIdOld
      }

      if (dailyDiscountRateForApi > 0) {
        params.dailyDiscountRate = dailyDiscountRateForApi
      }

      if (discountCouponCtx.hasDiscount && discountCouponCtx.isValidForCompany && discountCouponCtx.couponCode !== '') {
        params.couponCode = discountCouponCtx.couponCode
      }

      return params
    }

    setStep3PricesLoading(true)
    try {
      let params = buildPricesParams(pricesSnap)
      let response = await apiGet('vehicles/prices', params, false)

      if (response?.success && response?.data && shouldIncludeDeliveryInPrice) {
        const data = response.data
        const listAj = Number(data?.ajarleeDeliveryCost) || 0
        const listOff = Number(data?.officeDeliveryCost) || 0
        const vAjAfter = validateWizardDiscountedDeliveryFee(listAj, ajStr, t)
        const vOffAfter = validateWizardDiscountedDeliveryFee(listOff, offStr, t)
        const firstSentAj = params.ajarleeDiscountedDeliveryCost != null
        const firstSentOff = params.officeDiscountedDeliveryCost != null
        const wantAj = ajStr !== '' && vAjAfter.ok && !vAjAfter.skip
        const wantOff = offStr !== '' && vOffAfter.ok && !vOffAfter.skip
        const needRetry = (wantAj && !firstSentAj) || (wantOff && !firstSentOff)
        if (needRetry) {
          step3PricesRef.current = data
          params = buildPricesParams(data)
          try {
            const response2 = await apiGet('vehicles/prices', params, false)
            if (response2?.success && response2?.data) {
              response = response2
            }
          } catch {
            /* keep first successful response if retry fails */
          }
        }
      }

      const priceError = readPriceError(response)
      if (priceError) {
        // Car-not-available (and similar) comes back as success:true + data.error — surface it and drop the
        // snapshot so the step-3 summary shows the message and "Add this car" (confirm) stays disabled.
        setStep3PriceError(priceError)
        setStep3Prices(null)
      } else if (response?.success && response?.data) {
        setStep3PriceError('')
        setStep3Prices(response.data)
      } else {
        setStep3PriceError('')
        setStep3Prices(null)
      }
    } catch {
      setStep3PriceError('')
      setStep3Prices(null)
    } finally {
      setStep3PricesLoading(false)
    }
  }, [
    selectedCar,
    bookingData?.rentalStartDate,
    bookingData?.rentalEndDate,
    bookingData?.dailyRate,
    bookingData?.dailyDiscountRate,
    bookingData?.rentalCarDailyRate,
    bookingData?.rentalCar?.company,
    bookingData?.isDiscountEdit,
    bookingData?.delivery?.type,
    bookingData?.deliveryId,
    bookingData?.delivery?._id,
    bookingData?.discount?._id,
    bookingData?.discount?.cupon,
    deliveryData,
    priceDecision,
    discountListDaily,
    discountDiscountDaily,
    discountSource,
    deliveryDiscountAjarleeOmr,
    deliveryDiscountCompanyOmr,
    t,
  ])

  useEffect(() => {
    if (currentStep !== 3) {
      setStep3Prices(null)
      setStep3DiscountError('')
      setStep3DeliveryFeeDiscountError('')
      setStep3PriceError('')
      return
    }
    const timer = setTimeout(() => {
      fetchStep3Prices()
    }, 450)
    return () => clearTimeout(timer)
  }, [currentStep, fetchStep3Prices])

  const handleNext = () => {
    if (currentStep === 1) setCurrentStep(2)
  }

  const formatDateForDelivery = dateString => {
    if (!dateString) return null
    const m = moment.utc(dateString)
    return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') + '.000' : null
  }

  const createDelivery = async (carOverride = null) => {
    const car = carOverride ?? selectedCar
    const resolvedDeliveryType = resolveWizardDeliveryType(bookingData)
    const isPickupMyself = resolvedDeliveryType === 'PICK_UP_MYSELF'

    if (!isPickupMyself && !car?.isDeliveryAvailable) return { ok: true, delivery: null }

    try {
      const pickupCoords = bookingData?.delivery?.pickupLocation?.coordinates
      const dropoffCoords = bookingData?.delivery?.dropOffLocation?.coordinates
      const pickupLat = bookingData?.pickupLat ?? pickupCoords?.[1] ?? bookingData?.lat
      const pickupLon = bookingData?.pickupLon ?? pickupCoords?.[0] ?? bookingData?.lon
      const dropoffLat = bookingData?.dropoffLat ?? dropoffCoords?.[1] ?? bookingData?.lat
      const dropoffLon = bookingData?.dropoffLon ?? dropoffCoords?.[0] ?? bookingData?.lon

      // Use userId from old booking (required by createDeliveryForUser API)
      const oldBookingUserId = bookingData?.user?._id || bookingData?.user?.id || bookingData?.user || bookingData?.userId
      if (!oldBookingUserId) {
        notify.error(t('updateBooking.messages.userNotFoundInBooking') || 'User not found in booking')
        return { ok: false, delivery: null }
      }

      const deliveryRequestType = resolvedDeliveryType

      const requestData = {
        rentalCarId: car._id,
        userId: oldBookingUserId,
        type: deliveryRequestType,
        booking: {
          from: formatDateForDelivery(bookingData?.rentalStartDate),
          to: formatDateForDelivery(bookingData?.rentalEndDate),
        },
        pickupLocation: {
          lat: parseFloat(pickupLat) || 0,
          lon: parseFloat(pickupLon) || 0,
          pickupLocationAddress: bookingData?.pickupLocationAddress || bookingData?.delivery?.pickupLocationAddress || bookingData?.address || '',
        },
        dropOffLocation: {
          lat: parseFloat(dropoffLat) || 0,
          lon: parseFloat(dropoffLon) || 0,
          dropOffLocationAddress: bookingData?.dropOffLocationAddress || bookingData?.delivery?.dropOffLocationAddress || bookingData?.address || '',
        },
      }

      const response = await apiPost('deliveryy', requestData)
      if (response?.success || response?.code === 200) {
        setDeliveryData(response?.data)
        lastCreatedDeliveryTypeRef.current = deliveryRequestType
        return { ok: true, delivery: response?.data }
      }
      notify.error(response?.message || 'Failed to create delivery')
      return { ok: false, delivery: null }
    } catch (error) {
      notify.error(error?.message || 'Failed to create delivery')
      return { ok: false, delivery: null }
    }
  }

  const handleCarSelected = async car => {
    setSelectedCar(car)
    setCarActionInProgressId(car._id)
    try {
      // Discounts have moved to the Edit Booking screen: selecting a car completes the swap directly
      // (no discount step). The caller then refreshes (unpaid, in-place) or opens the new booking (paid).
      const result = await createDelivery(car)
      if (result?.ok) {
        await handleConfirm(car, result.delivery)
      }
    } finally {
      setCarActionInProgressId(null)
    }
  }

  const computeDailyRates = (carArg = null) => {
    const car = carArg || selectedCar

    // CHANGE-PRICE with no ADMIN (dashboard) daily discount to carry: the NEW car's own pricing governs
    // entirely. A discount that comes from the car's OWN pricing (an in-window weeklyDiscountRate/
    // dailyDiscountRate promo, stored on the booking as dailyRate vs dailyDiscountRate but never recorded in
    // discountControl) belongs to the OLD car and must NOT carry. KEEP-SAME is intentionally excluded — it
    // holds the customer's OLD applied rate on the new car (handled by the KEEP_SAME branch below), e.g. old
    // applied 10 → new base 100 keeps applied 10 with a 90 discount, NOT the new car's own 20 promo.
    const oldHasAdminDailyDiscount = isArray(bookingData?.discountControl) && some(bookingData.discountControl, r => r?.target === 'daily' && Number(r?.value) > 0)
    if (priceDecision === PRICE_OPTIONS.CHANGE && discountSource === DISCOUNT_SOURCE.NONE && !oldHasAdminDailyDiscount) {
      const newCarDaily = Number(car?.dailyRate) || 0
      const newCarDailyDisc = Number(car?.dailyDiscountRate) || 0
      // New car has its OWN in-window promo (weeklyDiscountRate → dailyDiscountRate): show that as the
      // discount (e.g. new daily 150, discounted 123 → 27 discount), NOT the old car's promo.
      if (newCarDailyDisc > 0 && newCarDailyDisc < newCarDaily) {
        return { dailyRate: newCarDaily, dailyDiscountRate: newCarDailyDisc }
      }
      // Plain period tier (sticker-vs-period gap, no promo): omit forced rates so the backend applies the
      // new car's own period rate with no phantom discount (e.g. daily 8 / weekly 35 → 5/day, no discount).
      if (isOldBookingPeriodRateOnly(bookingData) || isNewCarPeriodPriced(car)) {
        return { dailyRate: undefined, dailyDiscountRate: undefined }
      }
      // Plain new car, no discount at all.
      return { dailyRate: newCarDaily, dailyDiscountRate: 0 }
    }

    const newCarDailyRate = car?.dailyRate || 0
    const newCarDailyDiscountRate = car?.dailyDiscountRate || 0
    const actualDailyRate = car?.actualDailyRate || newCarDailyRate
    const oldEffectiveRate = bookingData?.dailyDiscountRate > 0 ? bookingData.dailyDiscountRate : bookingData?.dailyRate || 0
    const oldAppliedDailyRate = getOldBookingAppliedDailyRate(bookingData)
    const newCarIsCheaperThanOldApplied = newCarDailyRate > 0 && newCarDailyRate < oldAppliedDailyRate

    if (priceDecision === PRICE_OPTIONS.KEEP_SAME) {
      if (newCarIsCheaperThanOldApplied) {
        if (discountSource === DISCOUNT_SOURCE.AJARLEE || discountSource === DISCOUNT_SOURCE.COMPANY) {
          const { listDaily } = parseWizardListDaily()
          const v = validateWizardDiscountDaily(listDaily, discountDiscountDaily, {
            allowDiscountEqualToList: true,
          })
          if (!v.skip && v.ok && v.discountedRate > 0 && !Number.isNaN(listDaily) && listDaily > 0) {
            return { dailyRate: listDaily, dailyDiscountRate: v.discountedRate }
          }
        }
        // No discount source picked and no admin discount to carry: the NEW (cheaper) car's own pricing
        // governs. `car.dailyRate` is already the period per-day (e.g. weeklyRate/7), `car.actualDailyRate`
        // the sticker. Two sub-cases the admin expects on the swap:
        //  A) an in-window weekly discount applies (dailyDiscountRate > 0) → show the weekly per-day
        //     (dailyRate) as the daily rate, the discounted weekly per-day (dailyDiscountRate) as the applied
        //     rate, and the gap between them as the daily discount.
        //  B) only the weekly rate applies, no in-window discount → show the weekly per-day as BOTH the daily
        //     rate and the applied rate, with NO discount. Do NOT turn the sticker-vs-weekly gap
        //     (actualDailyRate − dailyRate) into a phantom discount that then exceeds the daily rate.
        if (discountSource === DISCOUNT_SOURCE.NONE && !oldHasAdminDailyDiscount) {
          if (newCarDailyDiscountRate > 0 && newCarDailyDiscountRate < newCarDailyRate) {
            return { dailyRate: newCarDailyRate, dailyDiscountRate: newCarDailyDiscountRate }
          }
          return { dailyRate: newCarDailyRate, dailyDiscountRate: 0 }
        }
        const nativeDisc = getNewCarNativeDiscountRate(car)
        if (nativeDisc > 0 && nativeDisc < newCarDailyRate) {
          return { dailyRate: newCarDailyRate, dailyDiscountRate: nativeDisc }
        }
        if (actualDailyRate > newCarDailyRate) {
          return { dailyRate: actualDailyRate, dailyDiscountRate: newCarDailyRate }
        }
        return { dailyRate: newCarDailyRate, dailyDiscountRate: newCarDailyDiscountRate || 0 }
      }
      if (discountSource === DISCOUNT_SOURCE.AJARLEE || discountSource === DISCOUNT_SOURCE.COMPANY) {
        const { listDaily } = parseWizardListDaily()
        const v = validateWizardDiscountDaily(listDaily, discountDiscountDaily, {
          allowDiscountEqualToList: false,
        })
        if (!v.skip && v.ok && v.discountedRate > 0 && !Number.isNaN(listDaily) && listDaily > 0) {
          return { dailyRate: listDaily, dailyDiscountRate: v.discountedRate }
        }
        if (v.skip) {
          const listForRate = !Number.isNaN(listDaily) && listDaily > 0 ? listDaily : newCarDailyRate
          return { dailyRate: listForRate, dailyDiscountRate: oldEffectiveRate }
        }
      }
      return { dailyRate: newCarDailyRate, dailyDiscountRate: oldEffectiveRate }
    }

    if (priceDecision === PRICE_OPTIONS.CHANGE && (discountSource === DISCOUNT_SOURCE.AJARLEE || discountSource === DISCOUNT_SOURCE.COMPANY)) {
      const { listDaily } = parseWizardListDaily()
      const v = validateWizardDiscountDaily(listDaily, discountDiscountDaily)
      if (!v.skip && v.ok && v.discountedRate > 0 && !Number.isNaN(listDaily) && listDaily > 0) {
        return { dailyRate: listDaily, dailyDiscountRate: v.discountedRate }
      }
    }

    // Change price: carry the OLD booking's per-day discount amount onto the NEW car's rate, so an
    // existing daily-rate discount keeps applying (on the new rate) after the swap.
    const oldListDaily = getOldBookingListDailyRate(bookingData)
    const oldAppliedDaily = getOldBookingAppliedDailyRate(bookingData)
    const oldDailyDiscountPerDay = oldListDaily > 0 && oldAppliedDaily > 0 && oldAppliedDaily < oldListDaily ? oldListDaily - oldAppliedDaily : 0
    if (oldDailyDiscountPerDay > 0 && oldDailyDiscountPerDay < newCarDailyRate) {
      return { dailyRate: newCarDailyRate, dailyDiscountRate: Math.max(0, newCarDailyRate - oldDailyDiscountPerDay) }
    }

    if (newCarDailyDiscountRate && newCarDailyDiscountRate > 0) {
      return { dailyRate: newCarDailyRate, dailyDiscountRate: newCarDailyDiscountRate }
    }
    if (actualDailyRate > newCarDailyRate) {
      return { dailyRate: actualDailyRate, dailyDiscountRate: newCarDailyRate }
    }
    return { dailyRate: newCarDailyRate, dailyDiscountRate: newCarDailyDiscountRate || 0 }
  }

  const formatDateForAPI = dateString => {
    if (!dateString) return null
    const m = moment.utc(dateString)
    return m.isValid() ? m.toISOString() : null
  }

  const handleConfirm = async (carOverride = null, deliveryOverride = null) => {
    setIsSubmitting(true)

    // car / delivery may be passed in directly (car selection triggers confirm before React state
    // has committed setSelectedCar / setDeliveryData), so prefer the overrides over state.
    const car = carOverride || selectedCar
    const dData = deliveryOverride || deliveryData

    if (priceDecision === PRICE_OPTIONS.CHANGE) {
      if (discountSource === DISCOUNT_SOURCE.AJARLEE || discountSource === DISCOUNT_SOURCE.COMPANY) {
        const { listDaily } = parseWizardListDaily()
        const v = validateWizardDiscountDaily(listDaily, discountDiscountDaily)
        if (!v.skip && !v.ok) {
          notify.error(v.error)
          setIsSubmitting(false)
          return
        }
      }
    } else if (priceDecision === PRICE_OPTIONS.KEEP_SAME) {
      const newCarDailyRate = car?.dailyRate || 0
      const oldAppliedDailyRate = getOldBookingAppliedDailyRate(bookingData)
      const newCarIsCheaperThanOldApplied = newCarDailyRate > 0 && newCarDailyRate < oldAppliedDailyRate
      if (discountSource === DISCOUNT_SOURCE.AJARLEE || discountSource === DISCOUNT_SOURCE.COMPANY) {
        const { listDaily } = parseWizardListDaily()
        const v = validateWizardDiscountDaily(listDaily, discountDiscountDaily, {
          allowDiscountEqualToList: newCarIsCheaperThanOldApplied,
        })
        if (!v.skip && !v.ok) {
          notify.error(v.error)
          setIsSubmitting(false)
          return
        }
      }
    }

    const includeDeliveryInConfirm = getWizardShouldIncludeDeliveryInPrice(bookingData, dData, lastCreatedDeliveryTypeRef)
    if (includeDeliveryInConfirm) {
      const listAj = Number(step3Prices?.ajarleeDeliveryCost)
      const vAj = validateWizardDiscountedDeliveryFee(listAj, deliveryDiscountAjarleeOmr, t)
      if (!vAj.skip && !vAj.ok) {
        notify.error(vAj.error)
        setIsSubmitting(false)
        return
      }
      const listOff = Number(step3Prices?.officeDeliveryCost)
      const vOff = validateWizardDiscountedDeliveryFee(listOff, deliveryDiscountCompanyOmr, t)
      if (!vOff.skip && !vOff.ok) {
        notify.error(vOff.error)
        setIsSubmitting(false)
        return
      }
    }

    const { dailyRate, dailyDiscountRate } = computeDailyRates(car)
    const discountCouponCtx = getBookingDiscountCouponContext(bookingData, car?.company)

    try {
      const payload = {
        rentalCarId: car._id,
        cancelPaymentType: 'none',
        rentalStartDate: formatDateForAPI(bookingData?.rentalStartDate),
        rentalEndDate: formatDateForAPI(bookingData?.rentalEndDate),
      }
      // Only force the daily rates when computeDailyRates resolved them. For a period-rate booking
      // with no admin discount they are left undefined so the backend applies the new car's own
      // period rate (weekly/monthly/yearly) instead of a phantom sticker-vs-period discount.
      if (dailyRate != null) {
        payload.dailyRate = dailyRate
        payload.dailyDiscountRate = dailyDiscountRate || 0
      }
      if (priceDecision === PRICE_OPTIONS.CHANGE || priceDecision === PRICE_OPTIONS.KEEP_SAME) {
        if (discountSource === DISCOUNT_SOURCE.AJARLEE) {
          payload.discountSource = DISCOUNT_SOURCE.AJARLEE
        } else if (discountSource === DISCOUNT_SOURCE.COMPANY) {
          payload.discountSource = DISCOUNT_SOURCE.COMPANY
          const reason = trim(discountReason || '')
          if (reason) {
            payload.discountReason = reason
          }
        }
      }
      if (includeDeliveryInConfirm) {
        const listAj = Number(step3Prices?.ajarleeDeliveryCost) || 0
        const vAj = validateWizardDiscountedDeliveryFee(listAj, deliveryDiscountAjarleeOmr, t)
        if (vAj.ok && !vAj.skip) {
          payload.ajarleeDiscountedDeliveryCost = vAj.discountedFee
        }
        const listOff = Number(step3Prices?.officeDeliveryCost) || 0
        const vOff = validateWizardDiscountedDeliveryFee(listOff, deliveryDiscountCompanyOmr, t)
        if (vOff.ok && !vOff.skip) {
          payload.officeDiscountedDeliveryCost = vOff.discountedFee
        }
        const discountedDeliveryProvided = (!vAj.skip && vAj.ok) || (!vOff.skip && vOff.ok)
        const bothDiscountFieldsProvided = !vAj.skip && vAj.ok && !vOff.skip && vOff.ok
        const eqEps = 1e-4
        const bothDiscountedEqualList =
          bothDiscountFieldsProvided &&
          Number.isFinite(listAj) &&
          Number.isFinite(listOff) &&
          Math.abs(vAj.discountedFee - listAj) <= eqEps &&
          Math.abs(vOff.discountedFee - listOff) <= eqEps
        if (bothDiscountedEqualList) {
          payload.discountedDeliveryCost = 0
        } else if (discountedDeliveryProvided && step3Prices) {
          const newDeliveryCost = Number(step3Prices.deliveryCost)
          if (Number.isFinite(newDeliveryCost)) {
            payload.discountedDeliveryCost = newDeliveryCost
          }
        }
      }
      if (dData?.id || dData?._id) {
        payload.deliveryId = dData.id || dData._id
      }
      if (discountCouponCtx.hasDiscount && discountCouponCtx.isValidForCompany && discountCouponCtx.couponCode !== '') {
        payload.couponCode = discountCouponCtx.couponCode
      }
      // Edit Booking flow: don't fire the car-swap request on "Add this car". Hand the fully-built
      // payload + selected car up to the parent, which fires it on the main "Confirm update" button.
      if (onStageCarSwap) {
        // Pass the freshly-created delivery (office / service / combined cost + priced totals) so the Edit
        // screen shows and persists the NEW car's delivery cost and service fee instead of the old ones.
        onStageCarSwap({ payload, car, delivery: dData, priceDecision })
        setIsSubmitting(false)
        return
      }

      const response = await apiPost(`booking/cancel/${bookingId}`, payload)

      if (response?.success) {
        onCancelSuccess?.(response?.data?.newBookingId || null)
      } else {
        notify.error(response?.message || t('updateBooking.messages.failedToSaveBooking'))
        setIsSubmitting(false)
      }
    } catch (error) {
      notify.error(error?.message || t('updateBooking.messages.failedToSaveBooking'))
      setIsSubmitting(false)
    }
  }

  const handleBackFromStep2 = () => setCurrentStep(1)
  const handleBackFromStep3 = () => setCurrentStep(2)

  // Step 1: Price Decision
  const renderStep1 = () => (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('updateBooking.changeCarWizard.step1.title')}</h3>
      <p className="text-sm text-gray-600 mb-6">{t('updateBooking.changeCarWizard.step1.description')}</p>

      <div className="space-y-4">
        <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-dark-600 has-[:checked]:bg-dark-50">
          <input
            type="radio"
            name="priceDecision"
            value={PRICE_OPTIONS.KEEP_SAME}
            checked={priceDecision === PRICE_OPTIONS.KEEP_SAME}
            onChange={() => {
              // Discounts are applied on the Edit Booking screen now, so the swap itself carries no
              // discount source — keep NONE for both price decisions.
              setPriceDecision(PRICE_OPTIONS.KEEP_SAME)
              setDiscountSource(DISCOUNT_SOURCE.NONE)
            }}
            className="h-4 w-4 text-dark-600 focus:ring-dark-500 border-gray-300"
          />
          <span className="ml-3 text-sm font-medium text-gray-900">{t('updateBooking.changeCarWizard.step1.keepSamePrices')}</span>
        </label>

        <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-dark-600 has-[:checked]:bg-dark-50">
          <input
            type="radio"
            name="priceDecision"
            value={PRICE_OPTIONS.CHANGE}
            checked={priceDecision === PRICE_OPTIONS.CHANGE}
            onChange={() => {
              setPriceDecision(PRICE_OPTIONS.CHANGE)
              setDiscountSource(DISCOUNT_SOURCE.NONE)
            }}
            className="h-4 w-4 text-dark-600 focus:ring-dark-500 border-gray-300"
          />
          <span className="ml-3 text-sm font-medium text-gray-900">{t('updateBooking.changeCarWizard.step1.changePrice')}</span>
        </label>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={handleNext}
          className="px-4 py-2 text-sm font-medium text-white bg-dark-600 rounded-md hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {t('updateBooking.changeCarWizard.next')}
        </button>
      </div>
    </div>
  )

  // Step 2: Car Selection
  const renderStep2 = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200">
        <button type="button" onClick={handleBackFromStep2} className="text-sm font-medium text-gray-600 hover:text-gray-900">
          ← {t('updateBooking.changeCarWizard.back')}
        </button>
      </div>
      <div className="flex-grow overflow-hidden">
        <AddRentalCar
          bookingData={bookingData}
          loadingCarId={loadingCarId}
          carActionInProgressId={carActionInProgressId}
          selectedCarId={selectedCar?._id}
          onSelectCar={handleCarSelected}
          isAdmin={isAdmin}
        />
      </div>
    </div>
  )

  const formatOmr = v => {
    const n = Number(v)
    return Number.isFinite(n) ? n.toFixed(3) : '0.000'
  }

  /** Delivery list / summary delivery line: 2 decimal places (avoid 3-decimal OMR display on delivery fields). */
  const formatDeliveryCostDisplay = v => {
    const n = Number(v)
    return Number.isFinite(n) ? n.toFixed(2) : '—'
  }

  const formatDeliveryListFeePlain = v => {
    const n = Number(v)
    if (!Number.isFinite(n)) return '—'
    return String(parseFloat(n.toFixed(10)))
  }

  // Step 3: Discount Source
  const renderStep3 = () => {
    const carLabel = selectedCar ? `${selectedCar.car?.make || ''} ${selectedCar.car?.model || ''} (${selectedCar.modelYear || ''})` : ''
    const isStep3DeliverToMe = getWizardShouldIncludeDeliveryInPrice(bookingData, deliveryData, lastCreatedDeliveryTypeRef)
    const discountCouponCtx = getBookingDiscountCouponContext(bookingData, selectedCar?.company)
    const priceAjarleeList = Number(step3Prices?.ajarleeDeliveryCost)
    const priceOfficeList = Number(step3Prices?.officeDeliveryCost)
    const showAjarleeList = Number.isFinite(priceAjarleeList) ? formatDeliveryListFeePlain(priceAjarleeList) : '—'
    const showOfficeList = Number.isFinite(priceOfficeList) ? formatDeliveryListFeePlain(priceOfficeList) : '—'
    const pricesReadyForConfirm = !step3PricesLoading && step3Prices != null

    return (
      <div className="p-6 overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('updateBooking.changeCarWizard.step3.title')}</h3>
        <p className="text-sm text-gray-600 mb-4">{t('updateBooking.changeCarWizard.step3.description')}</p>
        {discountCouponCtx.hasDiscount && !discountCouponCtx.isValidForCompany && (
          <p className="text-sm text-red-600 mb-4">{t('updateBooking.changeCarWizard.step3.oldBookingCouponInvalidForCar')}</p>
        )}

        {selectedCar && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-medium text-gray-900 mb-3">{trim(carLabel)}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{t('updateBooking.paymentSummary.title')}</div>
            {step3PricesLoading ? (
              <div className="flex justify-center py-6">
                <LoadingIcon />
              </div>
            ) : step3Prices ? (
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-600">{t('updateBooking.paymentSummary.rentPricePerDay')}</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {(() => {
                      const dr = step3Prices.dailyRate ?? selectedCar.dailyRate
                      const ddr = step3Prices.dailyDiscountRate
                      const hasDiscount = ddr != null && ddr !== '' && Number(ddr) > 0 && Number(ddr) < Number(dr || 0)
                      const perDay = hasDiscount ? ddr : dr
                      return `${formatOmr(perDay)} ${t('common.omr')}`
                    })()}
                  </dd>
                </div>
                {step3Prices.numberOfDays != null && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">{t('updateBooking.paymentSummary.rentalDuration')}</dt>
                    <dd className="font-medium text-gray-900">
                      {Number(step3Prices.numberOfDays)} {t('updateBooking.paymentSummary.days')}
                    </dd>
                  </div>
                )}
                {step3Prices.priceWithDays != null && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">{t('updateBooking.changeCarWizard.step3.priceWithDays')}</dt>
                    <dd className="font-medium text-gray-900">
                      {formatOmr(step3Prices.priceWithDays)} {t('common.omr')}
                    </dd>
                  </div>
                )}
                {step3Prices &&
                  (() => {
                    const couponCtx = getBookingDiscountCouponContext(bookingData, selectedCar?.company)
                    if (!couponCtx.hasDiscount || !couponCtx.isValidForCompany) return null
                    const bookingDisc = bookingData.discount
                    const listDaily = Number(step3Prices.dailyRate ?? selectedCar?.dailyRate) || 0
                    const daysCount = Number(step3Prices.numberOfDays) || 0
                    const rawPw = step3Prices.priceWithDays
                    const pricedTotal = rawPw != null && rawPw !== '' && Number.isFinite(Number(rawPw)) ? Number(rawPw) : Number.NaN
                    const canCompute = daysCount > 0 && listDaily > 0 && Number.isFinite(pricedTotal)
                    const baseListRent = canCompute ? listDaily * daysCount : null
                    const promoDiscountOmr = canCompute && baseListRent != null && baseListRent >= pricedTotal ? baseListRent - pricedTotal : 0
                    const amountDisplay = getBookingDiscountAmountDisplay(bookingDisc, formatOmr, t, promoDiscountOmr)
                    const rentalTotalForRemaining = Number.isFinite(pricedTotal) ? pricedTotal : canCompute && baseListRent != null ? baseListRent : 0
                    const discountPriceOmr = getBookingDiscountPriceOmr(bookingDisc, rentalTotalForRemaining, promoDiscountOmr)
                    const baseAfterDiscount = Math.max(0, rentalTotalForRemaining - discountPriceOmr)
                    return (
                      <>
                        <div className="flex justify-between gap-4 text-emerald-800">
                          <dt className="font-medium">{buildBookingPromoDiscountLabel(bookingDisc, t)}</dt>
                          <dd className="font-medium text-right tabular-nums">{amountDisplay}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-gray-600">{t('updateBooking.changeCarWizard.step3.basePriceAfterDiscount')}</dt>
                          <dd className="font-medium text-gray-900 text-right tabular-nums">
                            {formatOmr(baseAfterDiscount)} {t('common.omr')}
                          </dd>
                        </div>
                      </>
                    )
                  })()}
                {step3Prices.deliveryCost != null && Number(step3Prices.officeDeliveryCost) > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">{t('updateBooking.paymentSummary.deliveryCost')}</dt>
                    <dd className="font-medium text-gray-900">
                      {formatDeliveryCostDisplay(step3Prices.officeDeliveryCost)} {t('common.omr')}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-600">{t('updateBooking.paymentSummary.subtotal')}</dt>
                  <dd className="font-medium text-gray-900">
                    {formatOmr(step3Prices.amountWithoutInsurance ?? step3Prices.priceWithDays)} {t('common.omr')}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-600">{t('updateBooking.changeCarWizard.step3.vat')}</dt>
                  <dd className="font-medium text-gray-900">
                    {formatOmr(step3Prices.vatTaxWithoutInsurance)} {t('common.omr')}
                  </dd>
                </div>
                {step3Prices.deliveryCost != null && Number(step3Prices.ajarleeDeliveryCost) > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">{t('booking.orderSummary.serviceFees')}</dt>
                    <dd className="font-medium text-gray-900">
                      {formatDeliveryCostDisplay(step3Prices.ajarleeDeliveryCost)} {t('common.omr')}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-4 border-t border-gray-200 pt-2">
                  <dt className="text-base font-medium text-gray-900">{t('updateBooking.paymentSummary.totalPrice')}</dt>
                  <dd className="text-base font-semibold text-gray-900">
                    {formatOmr(step3Prices.totalAmountWithoutInsurance)} {t('common.omr')}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-sm text-gray-500">{t('updateBooking.changeCarWizard.step3.pricesUnavailable')}</p>
            )}
          </div>
        )}

        {(priceDecision === PRICE_OPTIONS.CHANGE || priceDecision === PRICE_OPTIONS.KEEP_SAME) && (
          <>
            <div className="space-y-4 mb-6">
              {priceDecision === PRICE_OPTIONS.CHANGE && (
                <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-dark-600 has-[:checked]:bg-dark-50">
                  <input
                    type="radio"
                    name="discountSource"
                    value={DISCOUNT_SOURCE.NONE}
                    checked={discountSource === DISCOUNT_SOURCE.NONE}
                    onChange={() => {
                      setDiscountSource(DISCOUNT_SOURCE.NONE)
                      setDiscountDiscountDaily('')
                      setDiscountReason('')
                      setStep3DiscountError('')
                      setDeliveryDiscountAjarleeOmr('')
                      setDeliveryDiscountCompanyOmr('')
                      setStep3DeliveryFeeDiscountError('')
                      if (selectedCar?.dailyRate != null && selectedCar.dailyRate !== '') {
                        setDiscountListDaily(String(selectedCar.dailyRate))
                      }
                    }}
                    className="h-4 w-4 text-dark-600 focus:ring-dark-500 border-gray-300"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900">{t('updateBooking.changeCarWizard.step3.noDiscount')}</span>
                </label>
              )}

              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-dark-600 has-[:checked]:bg-dark-50">
                <input
                  type="radio"
                  name="discountSource"
                  value={DISCOUNT_SOURCE.AJARLEE}
                  checked={discountSource === DISCOUNT_SOURCE.AJARLEE}
                  onChange={() => {
                    setDiscountSource(DISCOUNT_SOURCE.AJARLEE)
                    applyCarRatesToAjarleeCompanyFields(selectedCar, setDiscountListDaily, setDiscountDiscountDaily)
                    setStep3DiscountError('')
                    setStep3DeliveryFeeDiscountError('')
                  }}
                  className="h-4 w-4 text-dark-600 focus:ring-dark-500 border-gray-300"
                />
                <span className="ml-3 text-sm font-medium text-gray-900">{t('updateBooking.changeCarWizard.step3.discountByAjarlee')}</span>
              </label>

              {discountSource === DISCOUNT_SOURCE.AJARLEE && (
                <div className="ml-7 pl-4 border-l-2 border-gray-200 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-ajarlee-daily-rate">
                        {t('updateBooking.labels.dailyRate')}
                      </label>
                      <input
                        id="wizard-ajarlee-daily-rate"
                        type="number"
                        min="0"
                        step="0.001"
                        value={discountListDaily}
                        onChange={e => setDiscountListDaily(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-ajarlee-discount-rate">
                        {t('updateBooking.labels.dailyDiscountRate')}
                      </label>
                      <input
                        id="wizard-ajarlee-discount-rate"
                        type="number"
                        min="0"
                        step="0.001"
                        value={discountDiscountDaily}
                        onChange={e => setDiscountDiscountDaily(e.target.value)}
                        placeholder={t('updateBooking.changeCarWizard.step3.dailyDiscountPlaceholder')}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{t('updateBooking.changeCarWizard.step3.dailyDiscountRateHint')}</p>
                </div>
              )}

              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-dark-600 has-[:checked]:bg-dark-50">
                <input
                  type="radio"
                  name="discountSource"
                  value={DISCOUNT_SOURCE.COMPANY}
                  checked={discountSource === DISCOUNT_SOURCE.COMPANY}
                  onChange={() => {
                    setDiscountSource(DISCOUNT_SOURCE.COMPANY)
                    applyCarRatesToAjarleeCompanyFields(selectedCar, setDiscountListDaily, setDiscountDiscountDaily)
                    setStep3DiscountError('')
                    setStep3DeliveryFeeDiscountError('')
                  }}
                  className="h-4 w-4 text-dark-600 focus:ring-dark-500 border-gray-300"
                />
                <span className="ml-3 text-sm font-medium text-gray-900">{t('updateBooking.changeCarWizard.step3.discountByCompany')}</span>
              </label>

              {discountSource === DISCOUNT_SOURCE.COMPANY && (
                <div className="ml-7 pl-4 border-l-2 border-gray-200 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-company-daily-rate">
                        {t('updateBooking.labels.dailyRate')}
                      </label>
                      <input
                        id="wizard-company-daily-rate"
                        type="number"
                        min="0"
                        step="0.001"
                        value={discountListDaily}
                        onChange={e => setDiscountListDaily(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-company-discount-rate">
                        {t('updateBooking.labels.dailyDiscountRate')}
                      </label>
                      <input
                        id="wizard-company-discount-rate"
                        type="number"
                        min="0"
                        step="0.001"
                        value={discountDiscountDaily}
                        onChange={e => setDiscountDiscountDaily(e.target.value)}
                        placeholder={t('updateBooking.changeCarWizard.step3.dailyDiscountPlaceholder')}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{t('updateBooking.changeCarWizard.step3.dailyDiscountRateHint')}</p>
                </div>
              )}

              {isStep3DeliverToMe && (
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('updateBooking.changeCarWizard.step3.discountByAjarlee')}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-ajarlee-delivery-list">
                          {t('updateBooking.changeCarWizard.step3.ajarleeDeliveryFee')}
                        </label>
                        <input
                          id="wizard-ajarlee-delivery-list"
                          readOnly
                          value={showAjarleeList}
                          className="block w-full rounded-md border-gray-200 bg-gray-50 text-gray-900 shadow-sm sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-ajarlee-delivery-discounted">
                          {t('updateBooking.changeCarWizard.step3.ajarleeDiscountedDeliveryFee')}
                        </label>
                        <input
                          id="wizard-ajarlee-delivery-discounted"
                          type="number"
                          min="0"
                          step="0.01"
                          value={deliveryDiscountAjarleeOmr}
                          onChange={e => {
                            setDeliveryDiscountAjarleeOmr(e.target.value)
                            setStep3DeliveryFeeDiscountError('')
                          }}
                          placeholder={t('updateBooking.changeCarWizard.step3.dailyDiscountPlaceholder')}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('updateBooking.changeCarWizard.step3.discountByCompany')}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-company-delivery-list">
                          {t('updateBooking.changeCarWizard.step3.companyDeliveryFee')}
                        </label>
                        <input
                          id="wizard-company-delivery-list"
                          readOnly
                          value={showOfficeList}
                          className="block w-full rounded-md border-gray-200 bg-gray-50 text-gray-900 shadow-sm sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="wizard-company-delivery-discounted">
                          {t('updateBooking.changeCarWizard.step3.companyDiscountedDeliveryFee')}
                        </label>
                        <input
                          id="wizard-company-delivery-discounted"
                          type="number"
                          min="0"
                          step="0.01"
                          value={deliveryDiscountCompanyOmr}
                          onChange={e => {
                            setDeliveryDiscountCompanyOmr(e.target.value)
                            setStep3DeliveryFeeDiscountError('')
                          }}
                          placeholder={t('updateBooking.changeCarWizard.step3.dailyDiscountPlaceholder')}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {step3PriceError ? <p className="text-sm text-red-600 mb-4">{step3PriceError}</p> : null}
            {step3DiscountError ? <p className="text-sm text-red-600 mb-4">{step3DiscountError}</p> : null}
            {step3DeliveryFeeDiscountError ? <p className="text-sm text-red-600 mb-4">{step3DeliveryFeeDiscountError}</p> : null}
          </>
        )}

        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button type="button" onClick={handleBackFromStep3} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
            ← {t('updateBooking.changeCarWizard.back')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || !pricesReadyForConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-dark-600 rounded-md hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('updateBooking.buttons.pleaseWait') : t('updateBooking.changeCarWizard.confirm')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
    </div>
  )
}

export default ChangeCarWizard
export { PRICE_OPTIONS, DISCOUNT_SOURCE, getOldBookingListDailyRate, getOldBookingAppliedDailyRate, getNewCarNativeDiscountRate, isOldBookingPeriodRateOnly, isNewCarPeriodPriced }
